import type { IRealIndex } from '@/music/ireal/parse';
import { playlistRefKey } from '@/state/store';
import { BUNDLED_PLAYLISTS } from './bundledPlaylists';
import { collectTuneSources, type TuneSourceEntry, type TuneSourceInputs } from './tuneSources';

/** Search spelling is deliberately looser than a tune's identity. In
 * particular, Moment’s Notice, MOMENT'S NOTICE and moments notice all match,
 * but their different editions remain separate results. */
export function normalizeTuneQuery(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/['’‘ʼ＇`´]/gu, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/** A shared filter label for equivalent catalog spellings. Keep the full
 * source description on each row; this only identifies its leading printed
 * key, so a signature/tonal-centre explanation does not make a new option. */
export function tuneSearchKeyLabel(value: string): string {
  const key = /^([A-Ga-g])([#b♯♭]?)(.*)$/.exec(value.trim());
  if (!key) return value;
  const [, letter, accidental, remainder] = key;
  const suffix = remainder.trim();
  const mode =
    /^(major|minor|maj|min)\b/i.exec(suffix)?.[1] ?? /^[mM-](?=$|[\s·,/(])/.exec(suffix)?.[0];
  // Bare iReal keys imply major. Do not silently reclassify unknown modes,
  // chord symbols or prose that happens to begin with a note letter.
  if (suffix && !mode) return value;
  const minor = mode === 'm' || mode === '-' || mode?.toLowerCase().startsWith('min');
  const symbol = accidental === '#' ? '♯' : accidental === 'b' ? '♭' : accidental;
  return `${letter.toUpperCase()}${symbol} ${minor ? 'minor' : 'major'}`;
}

type SearchableTune = Pick<TuneSourceEntry, 'title' | 'composer' | 'context'>;

/** All terms must occur in the title, composer or optional source context.
 * A title match comes before a composer match, then source context; an exact
 * title comes before a prefix or substring.
 * Never deduplicate by title: editions and playlist calls are meaningful. */
export function searchTuneRows<T extends SearchableTune>(rows: readonly T[], query: string): T[] {
  const normalized = normalizeTuneQuery(query);
  const terms = normalized ? normalized.split(' ') : [];
  return rows
    .map((row, index) => {
      const title = normalizeTuneQuery(row.title);
      const composer = normalizeTuneQuery(row.composer ?? '');
      const context = normalizeTuneQuery(row.context ?? '');
      if (
        !terms.every(
          (term) => title.includes(term) || composer.includes(term) || context.includes(term),
        )
      )
        return null;
      const titleTerms = terms.filter((term) => title.includes(term)).length;
      const rank = !normalized
        ? 0
        : title === normalized
          ? 0
          : title.startsWith(normalized)
            ? 1
            : terms.every((term) => title.split(' ').some((word) => word.startsWith(term)))
              ? 2
              : titleTerms === terms.length
                ? 3
                : terms.every((term) => title.includes(term) || composer.includes(term))
                  ? 4
                  : 5;
      return { row, index, title, rank, titleTerms };
    })
    .filter((entry) => entry !== null)
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        b.titleTerms - a.titleTerms ||
        a.title.localeCompare(b.title) ||
        a.index - b.index,
    )
    .map(({ row }) => row);
}

/** A bundled list's names: its own, and the one its file carries. */
const BUNDLED_NAMES = new Set(BUNDLED_PLAYLISTS.flatMap((list) => [list.name, list.fileName]));

/** The shipped chord library is searchable alongside a personal import. It
 * must never be loaded into the store's single imported-playlist slot just
 * to search it. Its rows reuse the ordinary load seam, including recents.
 *
 * Only identical iReal payloads in the two indexes are redundant. In
 * particular playlist rows must survive: two calls of the same tune can
 * carry different keys, tempos and performance notes. */
export function collectTuneSearchSources(
  inputs: TuneSourceInputs,
  bundled: IRealIndex | null,
): TuneSourceEntry[] {
  // A bundled playlist's charts carry the list's name as their context. It is
  // neither shown nor searched: beside the version label it says nothing, and
  // under its file's own name it names a borrowed collection, which the screen
  // never does. That holds in the import slot too, where the bundled list
  // sits once a tune link has opened from it.
  const rows = collectTuneSources(inputs).map((row) =>
    row.source === 'tune' && row.context && BUNDLED_NAMES.has(row.context)
      ? { ...row, context: undefined }
      : row,
  );
  if (!bundled) return rows;

  const imported = new Set(
    rows
      .filter((row) => row.source === 'tune' && row.ref?.kind === 'ireal')
      .map((row) => playlistRefKey(row.ref!)),
  );
  const bundledRows = collectTuneSources({
    presets: [],
    bundledScores: [],
    savedCharts: [],
    openbook: [],
    userPlaylists: [],
    playlist: bundled,
  }).filter((row) => !row.ref || !imported.has(playlistRefKey(row.ref)));

  return [
    ...rows,
    ...bundledRows.map((row) => ({
      ...row,
      key: `bundled-jazz-1460-${row.key}`,
      context: undefined,
    })),
  ];
}
